import React from 'react';
import { 
  Paper, Box, Typography, Button, List, ListItem, 
  ListItemText, ListItemSecondaryAction, IconButton, Chip, Stack, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ViewListIcon from '@mui/icons-material/ViewList'; // Add this import
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

export default function ThemesPanel({ 
  themes, 
  activeThemeId, 
  onThemeAdd, 
  onThemeEdit, 
  onThemeDelete, 
  onThemeSelect,
  onThemeReorder,
  onThemeGenerate,
  themeStats, // Add this new prop
  onViewBlocks // Add this new prop
}) {
  // Get total blocks across all levels for a theme
  const getTotalBlocks = (themeId) => {
    if (!themeStats || !themeStats[themeId]) return 0;
    
    const stats = themeStats[themeId];
    return Object.values(stats).reduce((total, levelStat) => total + levelStat.blocks, 0);
  };
  
  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Course Themes</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={onThemeAdd}
        >
          Add Theme
        </Button>
      </Box>
      
      <DragDropContext onDragEnd={onThemeReorder}>
        <Droppable droppableId="themes">
          {(provided) => (
            <List {...provided.droppableProps} ref={provided.innerRef}>
              {themes.length > 0 ? themes.map((theme, index) => {
                const totalBlocks = getTotalBlocks(theme.id);
                const stats = themeStats[theme.id] || {};
                
                return (
                  <Draggable key={theme.id} draggableId={theme.id} index={index}>
                    {(provided) => (
                      <ListItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{ 
                          border: '1px solid #eee', 
                          mb: 1, 
                          borderRadius: 1,
                          bgcolor: activeThemeId === theme.id ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                        onClick={() => onThemeSelect(theme.id)}
                      >
                        <Box display="flex" width="100%" alignItems="flex-start">
                          <div {...provided.dragHandleProps} style={{ marginRight: 10, paddingTop: 8 }}>
                            <DragIndicatorIcon />
                          </div>
                          <Box flexGrow={1}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {theme.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {theme.description || 'No description'}
                            </Typography>
                            
                            {/* Stats section */}
                            <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" sx={{ gap: 1 }}>
                              <Chip 
                                label={`${totalBlocks} blocks total`} 
                                size="small" 
                                color="primary"
                                variant="outlined"
                              />
                              
                              {[1, 2, 3].map(level => {
                                const levelStats = stats[level] || { blocks: 0, words: 0 };
                                return (
                                  <Chip 
                                    key={`level-${level}`}
                                    label={`L${level}: ${levelStats.blocks} blocks / ${levelStats.words} words`}
                                    size="small"
                                    color={levelStats.blocks > 0 ? 'default' : 'default'}
                                    variant={levelStats.blocks > 0 ? "filled" : "outlined"}
                                    sx={{ 
                                      opacity: levelStats.blocks > 0 ? 1 : 0.7,
                                      bgcolor: levelStats.blocks > 0 ? 
                                        (level === 1 ? 'success.100' : 
                                         level === 2 ? 'info.100' : 
                                         'warning.100') : 'inherit'
                                    }}
                                  />
                                );
                              })}
                            </Stack>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 8 }}>
                            {/* Add the View Blocks button */}
                            <Tooltip title="View content blocks">
                              <IconButton 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewBlocks(theme.id);
                                }}
                                size="small"
                                color="primary"
                              >
                                <ViewListIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Generate content">
                              <IconButton 
                                edge="end" 
                                color="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onThemeGenerate(theme.id);
                                }}
                                title="Generate content"
                              >
                                <AutoAwesomeIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          
                          <ListItemSecondaryAction>
                            <IconButton edge="end" onClick={(e) => {
                              e.stopPropagation();
                              onThemeEdit(theme);
                            }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton edge="end" onClick={(e) => {
                              e.stopPropagation();
                              onThemeDelete(theme.id);
                            }}>
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </Box>
                      </ListItem>
                    )}
                  </Draggable>
                );
              }) : (
                <Typography color="text.secondary" align="center" py={4}>
                  No themes yet. Create your first theme to get started.
                </Typography>
              )}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    </Paper>
  );
}
